'use client'

import CreateDialog from '@/shared/components/categories/CreateDialog'
import DeleteDialog from '@/shared/components/categories/DeleteDialog'
import EditDialog from '@/shared/components/categories/EditDialog'
import MenuDropdown from '@/shared/components/categories/MenuDropdown'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { useAdd } from '@/shared/hooks/categories/areas/useAdd'
import { useDelete, useDeleteMulti } from '@/shared/hooks/categories/areas/useDelete'
import useGetAreas from '@/shared/hooks/categories/areas/useGetArea'
import { useUpdate } from '@/shared/hooks/categories/areas/useUpdate'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useRegions } from '@/shared/hooks/useRegions'
import TabLayout, { ActionButton } from '@/shared/layouts/TabLayout'
import { createFileRoute, useLoaderData, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable, DataTableSorting } from '@workspace/ui/components/DataTable'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import dayjs from 'dayjs'
import { PlusIcon } from 'lucide-react'
import { Catalog } from 'node_modules/@workspace/lib/src/api/categories/catalog.type'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { set } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCan } from '@/shared/lib/authorization'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
})

export const Route = createFileRoute('/categories/_pathlessLayout/areas')({
    validateSearch: searchSchema,
    component: AreasPage,
})

function AreasPage() {
    const { links } = useLoaderData({ from: '/categories/_pathlessLayout' })
    const { t } = useTranslation()
    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const searchQuery = searchParams.q || ''
    const page = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : null

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [rowSelection, setRowSelection] = useState({})

    const [isCreateOpen, setCreateOpen] = useState(false)
    const [deleteCatalog, setDeleteCatalog] = useState<string | 'multiple' | null>(null)
    const [editOpen, setEditOpen] = useState<Catalog | boolean>(false)

    const debouncedSearch = useDebounce(searchQuery, 500)
    const { data: areas, isFetching } = useGetAreas({
        q: debouncedSearch,
        page,
        pageSize,
        sorting,
    })
    const { data: regions } = useRegions({
        enabled: !!isCreateOpen || !!editOpen,
    })
    const update = useUpdate()
    const deleteSingle = useDelete()
    const deleteMulti = useDeleteMulti()
    const add = useAdd()

    const totalPages = Math.ceil((areas?.meta?.itemCount || 0) / pageSize)
    const selectedCount = Object.keys(rowSelection)?.length
    const selectedIds = Object.keys(rowSelection)

    const handleCreate = async (values: { name: string; code: string; description?: string }) => {
        await add.mutateAsync({
            ...values,
            isActive: true,
        })
    }

    const handleBulkDeleteConfirm = async () => {
        if (selectedCount === 0) return

        const currentItemCount = areas?.data.length
        const willPageBeEmpty = currentItemCount === selectedCount && page > 1

        await deleteMulti.mutateAsync({ ids: selectedIds })
        setRowSelection({})

        if (willPageBeEmpty) {
            setPage(page - 1)
        }
    }

    const handleDelete = async () => {
        if (!deleteCatalog) return

        const currentItemCount = areas?.data.length
        const willPageBeEmpty = currentItemCount === 1 && page > 1

        await deleteSingle.mutateAsync({
            id: deleteCatalog,
        })
        setDeleteCatalog(null)
        setRowSelection({})
        if (willPageBeEmpty) {
            setPage(page - 1)
        }
    }

    const handleUpdateArea = async (values: any) => {
        await update.mutateAsync({
            id: values.id,
            data: {
                ...values,
                description: values.description || '',
            },
        })
    }

    const handleEditClick = useCallback((row: Catalog) => {
        setEditOpen(row)
    }, [])

    const handleDeleteClick = useCallback((id: string) => {
        setDeleteCatalog(id)
    }, [])

    const actions: ActionButton[] = [
        {
            text: t('categories.createAreaButton'),
            icon: PlusIcon,
            onClick: () => setCreateOpen(true),
            variant: 'default',
        },
    ]

    const showActionColumn = useCan('operate', 'library')

    const columns = useMemo<ColumnDef<Catalog>[]>(
        () => [
            ...(showActionColumn
                ? [
                      {
                          id: 'action',
                          accessorKey: 'action',
                          header: '',
                          size: 50,
                          cell: ({ row }: any) => (
                              <MenuDropdown
                                  id={row.original.id}
                                  onEdit={() => handleEditClick(row.original)}
                                  onDelete={handleDeleteClick}
                              />
                          ),
                          enableSorting: false,
                      },
                  ]
                : []),
            {
                accessorKey: 'code',
                header: t('categories.createArea.code'),
                enableSorting: true,
                meta: { className: 'px-2 py-2 text-sm' },
                cell: ({ getValue }) => <span className="text-sm">{getValue() as string}</span>,
            },
            {
                accessorKey: 'name',
                header: t('tabs.areas'),
                enableSorting: true,
                size: 200,
                meta: { className: 'px-2 py-2 text-sm' },
                cell: ({ row, getValue }) => (
                    <span className="text-sm cursor-pointer" onClick={() => handleEditClick(row.original)}>
                        {getValue() as string}
                    </span>
                ),
            },
            {
                accessorKey: 'region.name',
                header: t('tabs.regions'),
                enableSorting: true,
                meta: { className: 'px-2 py-2 text-sm' },
                cell: ({ getValue }) => <span className="text-sm">{getValue() as string}</span>,
            },
            {
                accessorKey: 'description',
                header: t('categories.table.description'),
                meta: { className: 'px-2 py-2 text-sm' },
                enableSorting: false,
                size: 300,
                cell: ({ getValue }) => <span className="text-sm">{getValue() as string}</span>,
            },
            {
                accessorKey: 'createdAt',
                header: t('categories.table.createdDate'),
                enableSorting: true,
                size: 140,
                meta: { className: 'px-2 py-2 text-sm' },
                cell: ({ getValue }) => {
                    const value = getValue() as string | undefined
                    return <span className="text-sm">{value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'}</span>
                },
            },
        ],
        [t, handleEditClick, handleDeleteClick, areas, page],
    )

    return (
        <>
            <TabLayout
                className="h-[calc(100vh-150px)] min-h-[500px] overflow-hidden"
                title={t('categories.title')}
                linksTo={links}
                actions={actions}
            >
                <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                    <div className="shrink-0 flex gap-4">
                        <BsSearchField
                            autoTrim
                            className="h-10 text-sm bg-background rounded-lg w-80"
                            value={searchQuery}
                            placeholder={t('categories.searchPlaceholder')}
                            onChange={value => setSearchQuery(value)}
                        />
                    </div>
                    <DataTable
                        isLoading={isFetching}
                        data={areas?.data || []}
                        columns={columns}
                        enableRowSelection={true}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        enableSorting={true}
                        sorting={sorting?.sortBy === 'region' ? { ...sorting, sortBy: 'region_name' } : sorting}
                        setSorting={sort => {
                            if (sort?.sortBy === 'region_name') {
                                return setSorting({
                                    ...sort,
                                    sortBy: 'region',
                                })
                            }
                            return setSorting(sort)
                        }}
                        emptyString={t('common.emptyState')}
                        containerClassName="flex-1 overflow-y-auto min-h-0"
                    />
                    <div className="shrink-0">
                        <TableDataFooter
                            selectedCount={selectedCount}
                            onDelete={handleBulkDeleteConfirm}
                            deleteDisabled={selectedCount === 0 || deleteMulti.isPending}
                            isLoading={deleteMulti.isPending}
                            totalCount={areas?.meta?.itemCount || 0}
                            currentPage={page}
                            pageSize={pageSize}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={value => setPageSize(value || 10)}
                        />
                    </div>
                </div>
            </TabLayout>
            <CreateDialog
                isOpen={isCreateOpen}
                listRegion={regions}
                onOpenChange={setCreateOpen}
                onConfirm={handleCreate}
                type="area"
            />
            <DeleteDialog
                isOpen={!!deleteCatalog}
                onOpenChange={isOpen => !isOpen && setDeleteCatalog(null)}
                onConfirm={handleDelete}
                isLoading={deleteSingle.isPending}
            />
            <EditDialog
                isOpen={!!editOpen}
                onOpenChange={setEditOpen}
                onConfirm={handleUpdateArea}
                type="area"
                value={editOpen}
                isLoading={update.isPending}
                listRegion={regions}
            />
        </>
    )
}
